import React, { useEffect, useCallback, useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';
import { RouteComponentProps } from 'react-router-dom';
import { FullState } from 'main/reducers';
import {
  fetchAlarmsForReporting,
  updateAlarmParameter,
} from 'ducks/alarmparameters';
import AlarmParametersView from './alarmparametersview';
import { Enum } from 'util/backendapi/models/api.interfaces';
import { useIsMounted, useShallowEqualSelector } from 'util/hooks';
import { usePlotState } from 'hooks/use-plot-state';
import { parseStringParamFromRouterProps } from 'util/routing';
import {
  resolveSettingsFromQuickplot,
  unmountPlotPage,
} from 'ducks/plot/scatter-time-series';
import { splitObservationPointItemUrlCode } from 'components/plots/timeseriesplotselectors';
import { isNotNull } from 'util/validation';
import { getCurrentDatetime, formatDatetimeForStorage } from 'util/dates';
import {
  fetchAlarmParameterScatterPlotData,
  RelativeAlarmScatterPlotData,
} from './relative-alarms-plot';
import { errorToString } from 'util/backendapi/error';
import {
  PlotSettingsAxis,
  QuickPlotAxisSide,
  TSPlotOnZoomFn,
  TSPlotZoomParams,
} from 'components/plots/timeseriesplot.types';
import { useGetApi } from 'hooks/use-get-api';
import { t } from '@lingui/macro';
import { i18n } from '@lingui/core';

type RouterProps = RouteComponentProps<{ obsPointCode: string }>;

export const AlarmParameters = (props: RouterProps) => {
  const dispatch = useDispatch();
  const isMounted = useIsMounted();
  const { alarms, instrumentTypeItem, isLoadingAlarms } =
    useShallowEqualSelector((state: FullState) => {
      return {
        isLoadingAlarms: state.alarmParameters.detail.isLoading,
        instrumentTypeItem: state.alarmParameters.detail.instrumentTypeItem,
        alarms: state.alarmParameters.detail.alarms,
      };
    });
  const [scatterPlotsData, setScatterPlotsData] = useState<
    RelativeAlarmScatterPlotData[]
  >([]);
  const [scatterPlotsErrorMessage, setScatterPlotsErrorMessage] =
    useState<string>('');
  const [isLoadingScatterPlots, setIsLoadingScatterPlots] =
    useState<boolean>(false);

  const [showPlots, setShowPlots] = useState<boolean>(false);

  const [quickPlotZoomParams, setQuickPlotZoomParams] =
    useState<TSPlotZoomParams | null>(null);

  const quickPlotState = usePlotState('quickPlot', Enum.PlotType.TIME_SERIES);

  const obsItemNumbers = parseStringParamFromRouterProps(props, 'obsPointCode');

  const relativeAlarmParameters = useMemo(() => {
    const currentDatetime = getCurrentDatetime();
    return alarms?.filter(
      (param) =>
        param.comparison_type ===
          Enum.AlarmParameter_COMPARISON_TYPE.relative &&
        param.status === Enum.AlarmParameter_STATUS.enabled &&
        param.relative_observation_point__code &&
        param.relative_item_number &&
        formatDatetimeForStorage(param.start_datetime) <= currentDatetime &&
        (param.end_datetime === null ||
          formatDatetimeForStorage(param.end_datetime) > currentDatetime)
    );
  }, [alarms]);

  const pairs = useMemo(() => {
    return obsItemNumbers
      .split(',')
      .map((pair) => splitObservationPointItemUrlCode(pair))
      .filter(isNotNull);
  }, [obsItemNumbers]);

  const [observationPoints, setUrl, setParams] = useGetApi(
    '' as '/observation-points/'
  );

  useEffect(() => {
    if (pairs) {
      setUrl('/observation-points/');
      setParams({ code: pairs[0]?.observationPointCode });
    }
  }, [pairs, setParams, setUrl]);

  const observationPoint = observationPoints.data?.[0] ?? null;
  const itemNumber = pairs[0]?.itemNumber ?? null;

  useEffect(() => {
    // Load / reload the quick plot (e.g. if the zoom changes)
    if (pairs.length > 0 && showPlots) {
      const startDatetime = quickPlotZoomParams
        ? quickPlotZoomParams.minDatetime
        : '';
      const endDatetime = quickPlotZoomParams
        ? quickPlotZoomParams.maxDatetime
        : '';
      const yAxes =
        (quickPlotZoomParams?.yAxes as PlotSettingsAxis<QuickPlotAxisSide>[]) ??
        [];

      dispatch(
        resolveSettingsFromQuickplot(
          pairs,
          startDatetime,
          endDatetime,
          '',
          yAxes,
          null,
          false,
          false,
          false
        )
      );
    }
  }, [showPlots, dispatch, pairs, quickPlotZoomParams, props]);

  const fetchRelativeAlarmPlotData = useCallback(async () => {
    setScatterPlotsData([]);
    setScatterPlotsErrorMessage('');

    if (relativeAlarmParameters && relativeAlarmParameters.length > 0) {
      setIsLoadingScatterPlots(true);
      try {
        const scatterPlotData = await fetchAlarmParameterScatterPlotData(
          relativeAlarmParameters
        );
        if (!isMounted()) return;

        setScatterPlotsData(scatterPlotData);
      } catch (e) {
        setScatterPlotsErrorMessage(errorToString(e));
      }
    }
    setIsLoadingScatterPlots(false);
  }, [
    isMounted,
    relativeAlarmParameters,
    setIsLoadingScatterPlots,
    setScatterPlotsErrorMessage,
    setScatterPlotsData,
  ]);

  useEffect(() => {
    if (showPlots) {
      fetchRelativeAlarmPlotData();
    }
  }, [
    showPlots,
    dispatch,
    fetchRelativeAlarmPlotData,
    relativeAlarmParameters,
  ]);

  const fetchData = useCallback(() => {
    // Fetch alarms when observationPoint or itemNumber change
    if (observationPoint && itemNumber) {
      dispatch(
        fetchAlarmsForReporting(observationPoint.id, itemNumber, '', '')
      );
    }
  }, [dispatch, observationPoint, itemNumber]);

  useEffect(() => {
    fetchData();
  }, [dispatch, fetchData]);

  const enableAlarm = useCallback(
    async (id: number, comment: string) => {
      await dispatch(
        updateAlarmParameter(
          id,
          {
            status: Enum.AlarmParameter_STATUS.enabled,
          },
          comment
        )
      );
      fetchData();
    },
    [dispatch, fetchData]
  );

  const disableAlarm = useCallback(
    async (id: number, reenable_datetime: string | null, comment: string) => {
      await dispatch(
        updateAlarmParameter(
          id,
          {
            status: Enum.AlarmParameter_STATUS.disabled,
            reenable_datetime: reenable_datetime,
          },
          comment
        )
      );
      fetchData();
    },
    [dispatch, fetchData]
  );

  const quickPlotZoom = useCallback<TSPlotOnZoomFn>(
    (zoomParams) => {
      setQuickPlotZoomParams(zoomParams);
    },
    [setQuickPlotZoomParams]
  );

  const resetQuickPlotZoom = useCallback(() => {
    setQuickPlotZoomParams(null);
  }, [setQuickPlotZoomParams]);

  // Clean up plot data when unmounting
  useEffect(
    () => () => {
      dispatch(unmountPlotPage());
    },
    [dispatch]
  );

  const errorMessage = observationPoints.isError
    ? errorToString(observationPoints.error)
    : !observationPoints.isLoading &&
      observationPoints.data &&
      observationPoints.data.length === 0
    ? i18n._(t`Observation point "${pairs[0].observationPointCode}" not found.`)
    : quickPlotState.errorMessage || scatterPlotsErrorMessage;

  const hasReadingSeriesErrorMessage = quickPlotState.readingsSeries.some(
    (rs) => rs.errorMessage && rs.errorMessage !== ''
  );

  const isLoadingQuickPlot =
    showPlots && quickPlotState.isLoading && !hasReadingSeriesErrorMessage;

  return (
    <AlarmParametersView
      obsPointCode={pairs.length ? pairs[0].observationPointCode : ''}
      observationPoint={observationPoint}
      readings={quickPlotState.readingsSeries}
      alarms={alarms}
      instrumentTypeItem={instrumentTypeItem}
      isLoadingQuickPlot={isLoadingQuickPlot}
      isLoadingAlarms={isLoadingAlarms}
      isLoadingScatterPlots={isLoadingScatterPlots}
      scatterPlotsData={scatterPlotsData}
      errorMessage={errorMessage}
      refreshAlarmsList={fetchData}
      enableAlarm={enableAlarm}
      disableAlarm={disableAlarm}
      onQuickPlotZoom={quickPlotZoom}
      onResetQuickPlotZoom={quickPlotZoomParams ? resetQuickPlotZoom : null}
      onSetShowPlots={setShowPlots}
      showPlots={showPlots}
      paddedMinDatetime={
        quickPlotZoomParams
          ? quickPlotZoomParams.minDatetime
          : quickPlotState.paddedMinDatetime
      }
      paddedMaxDatetime={
        quickPlotZoomParams
          ? quickPlotZoomParams.maxDatetime
          : quickPlotState.paddedMaxDatetime
      }
      yAxes={quickPlotZoomParams?.yAxes ?? []}
    />
  );
};

export default AlarmParameters;
